import { IconSearch, IconTrash, IconLoader2 } from "@tabler/icons-react";
import { useRef, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { bomsQueryOptions } from "@/features/dashboard/api/queries/bom-query";
import type { Bom } from "@/features/dashboard/types/bom";
import { formatNumber } from "@/utils/format-number";
import { Input } from "../core/forms/input";

export const parseJsonField = (field: unknown): Record<string, string | number> => {
    if (typeof field === "string") {
        try {
            return JSON.parse(field);
        } catch {
            return {};
        }
    }
    return (field as Record<string, string | number>) || {};
};

interface Props {
    projectName: string;
    onProjectNameChange: (value: string) => void;
    selectedItems: Bom[];
    onAddItem: (bom: Bom) => void;
    onRemoveItem: (id: string | null | undefined) => void;
    onQuantityChange: (id: string | null | undefined, qty: number) => void;
    supplierGrandTotal: number;
    companyGrandTotal: number;
    isOpen: boolean;
}

export function GenerateBomForm({
    projectName,
    onProjectNameChange,
    selectedItems,
    onAddItem,
    onRemoveItem,
    onQuantityChange,
    supplierGrandTotal,
    companyGrandTotal,
    isOpen,
}: Props) {
    const [searchTerm, setSearchTerm] = useState("");
    const [showDropdown, setShowDropdown] = useState(false);
    const searchContainerRef = useRef<HTMLDivElement>(null);

    const searchQuery = useQuery({
        ...bomsQueryOptions({
            "filter[search]": searchTerm || undefined,
            per_page: 10,
        }),
        enabled: isOpen,
    });

    const searchResults = searchQuery.data?.data || [];

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (
                searchContainerRef.current &&
                !searchContainerRef.current.contains(e.target as Node)
            ) {
                setShowDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleAdd = (bom: Bom) => {
        onAddItem(bom);
        setSearchTerm("");
        setShowDropdown(false);
    };

    return (
        <div className="flex flex-col gap-5 px-6 py-5 overflow-y-auto flex-1">
            {/* Project Name + Search row */}
            <div className="flex flex-wrap items-end gap-6">
                <div className="flex flex-col gap-1.5 min-w-[280px]">
                    <label className="text-sm font-medium text-gray-700">Project Name<span className="text-red-500">*</span></label>
                    <Input
                        type="text"
                        required
                        placeholder="E.g., Office Network Upgrade 2025"
                        value={projectName}
                        onChange={(e) => onProjectNameChange(e.target.value)}
                    />
                </div>

                <div className="flex flex-col gap-1.5 flex-1 min-w-[280px]">
                    <label className="text-sm font-medium text-gray-700">Add Items</label>
                    <div ref={searchContainerRef} className="relative">
                        <div className="relative">
                            <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4 pointer-events-none" />
                            <input
                                type="text"
                                placeholder="Search item name, brand, supplier..."
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setShowDropdown(true);
                                }}
                                onFocus={() => setShowDropdown(true)}
                                className="w-full pl-9 pr-9 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            {searchQuery.isFetching && (
                                <IconLoader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 animate-spin" />
                            )}
                        </div>
                        {/* search dropdown */}
                        {showDropdown && (
                            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                                {searchResults.length === 0 ? (
                                    <div className="px-4 py-3 text-sm text-gray-500 text-center">
                                        {searchQuery.isFetching ? "Searching..." : "No items found"}
                                    </div>
                                ) : (
                                    searchResults.map((bom) => {
                                        const alreadyAdded = selectedItems.some((i) => i.id === bom.id);
                                        return (
                                            <button
                                                key={bom.id}
                                                type="button"
                                                disabled={alreadyAdded}
                                                onClick={() => handleAdd(bom)}
                                                className="w-full text-left px-4 py-2.5 hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed border-b last:border-b-0 border-gray-100 transition-colors"
                                            >
                                                <div className="text-sm font-medium text-gray-800 capitalize">
                                                    {bom.item_name}
                                                    {alreadyAdded && (
                                                        <span className="ml-2 text-xs text-gray-400">(added)</span>
                                                    )}
                                                </div>
                                                <div className="text-xs text-gray-500 mt-0.5 capitalize">
                                                    {Array.isArray(bom.brand_model) ? bom.brand_model.join(", ") : bom.brand_model} &bull; {bom.supplier_name}
                                                </div>
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Items Table */}
            {selectedItems.length === 0 ? (
                <div className="flex-1 border border-dashed border-gray-200 rounded-lg flex items-center justify-center text-gray-400 text-sm min-h-[200px]">
                    No items added yet. Search and select items above.
                </div>
            ) : (
                <div className="overflow-auto border border-gray-200 rounded-lg flex-1">
                    <table className="w-full text-sm border-collapse">
                        <thead className="sticky top-0 z-10">
                            <tr className="bg-gray-50 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                                <th className="px-3 py-2.5 border-b border-gray-200 w-8">#</th>
                                <th className="px-3 py-2.5 border-b border-gray-200 min-w-[170px]">Item Name</th>
                                <th className="px-3 py-2.5 border-b border-gray-200 min-w-[150px]">Brand / Model</th>
                                <th className="px-3 py-2.5 border-b border-gray-200 min-w-[200px]">Specifications</th>
                                <th className="px-3 py-2.5 border-b border-gray-200 w-24">Warranty</th>
                                <th className="px-3 py-2.5 border-b border-gray-200 w-24">Qty</th>
                                <th className="px-3 py-2.5 border-b border-gray-200 w-16">Unit</th>
                                <th className="px-3 py-2.5 border-b border-gray-200 text-right min-w-[140px]">Supplier Unit Cost</th>
                                <th className="px-3 py-2.5 border-b border-gray-200 text-right min-w-[130px]">Supplier Total</th>
                                <th className="px-3 py-2.5 border-b border-gray-200 min-w-[180px]">Additional Costs</th>
                                <th className="px-3 py-2.5 border-b border-gray-200 min-w-[180px]">Markup Rate</th>
                                <th className="px-3 py-2.5 border-b border-gray-200 text-right min-w-[140px]">Company Unit Cost</th>
                                <th className="px-3 py-2.5 border-b border-gray-200 text-right min-w-[130px]">Company Total</th>
                                <th className="px-3 py-2.5 border-b border-gray-200 w-10" />
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {selectedItems.map((item, index) => {
                                const specs = parseJsonField(item.specifications);
                                const addCosts = parseJsonField(item.additional_costs);
                                const supplierEffTotal =
                                    (Number(item.supplier_unit_cost) || 0) *
                                    (Number(item.quantity) || 0);

                                return (
                                    <tr
                                        key={item.id}
                                        className="hover:bg-gray-50 transition-colors align-top"
                                    >
                                        <td className="px-3 py-2.5 text-gray-500">{index + 1}</td>
                                        <td className="px-3 py-2.5">
                                            <span className="font-medium text-gray-800 first-letter:uppercase">{item.item_name}</span>
                                            <div className="text-xs text-gray-400 mt-0.5 first-letter:uppercase">{item.supplier_name}</div>
                                        </td>
                                        <td className="px-3 py-2.5 text-gray-700 first-letter:uppercase">{Array.isArray(item.brand_model) ? item.brand_model.join(", ") : item.brand_model}</td>
                                        <td className="px-3 py-2.5">
                                            {Object.keys(specs).length > 0 ? (
                                                <div className="flex flex-col gap-0.5">
                                                    {Object.entries(specs).map(([k, v]) => (
                                                        <div key={k} className="text-xs">
                                                            <span className="font-medium text-gray-600 capitalize">{k}:</span>{" "}
                                                            <span className="text-gray-700">{String(v)}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-gray-400 text-xs">—</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <span className="font-medium text-gray-800">{item.warranty} {item.period}{item.warranty > 0 ? "s" : ""}</span>
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <input
                                                type="number"
                                                min="1"
                                                value={item.quantity ?? ""}
                                                onChange={(e) =>
                                                    onQuantityChange(
                                                        item.id,
                                                        e.target.value ? Number(e.target.value) : 0
                                                    )
                                                }
                                                className="w-20 px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </td>
                                        <td className="px-3 py-2.5 text-gray-600 capitalize">{item.unit}</td>
                                        <td className="px-3 py-2.5 text-right text-gray-700 tabular-nums">
                                            ₱{formatNumber(item.supplier_unit_cost)}
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-gray-700 tabular-nums">
                                            ₱{formatNumber(supplierEffTotal)}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            {Object.keys(addCosts).length > 0 ? (
                                                <div className="flex flex-col gap-0.5">
                                                    {Object.entries(addCosts).map(([k, v]) => (
                                                        <div key={k} className="text-xs">
                                                            <span className="font-medium text-gray-600 capitalize">{k}:</span>{" "}
                                                            <span className="text-gray-700">₱{formatNumber(Number(v))}</span>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span className="text-gray-400 text-xs">—</span>
                                            )}
                                        </td>
                                        {/* Markup rate */}
                                        <td className="px-3 py-2.5">
                                            <span className="font-medium text-gray-800">{item.markup_rate}%</span>
                                        </td>
                                        <td className="px-3 py-2.5 text-right text-gray-700 tabular-nums">
                                            ₱{formatNumber(item.company_unit_cost)}
                                        </td>
                                        <td className="px-3 py-2.5 text-right font-medium text-gray-800 tabular-nums">
                                            ₱{formatNumber(Number(item.company_total_cost))}
                                        </td>
                                        <td className="px-3 py-2.5">
                                            <button
                                                type="button"
                                                onClick={() => onRemoveItem(item.id)}
                                                className="text-red-400 hover:text-red-600 transition-colors"
                                            >
                                                <IconTrash size={15} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="bg-blue-50 font-semibold text-sm border-t-2 border-blue-200">
                                <td colSpan={8} className="px-3 py-3 text-right text-gray-700">
                                    Grand Total
                                </td>
                                <td className="px-3 py-3 text-right text-blue-700 tabular-nums">
                                    ₱{formatNumber(supplierGrandTotal)}
                                </td>
                                <td className="px-3 py-3" />
                                <td className="px-3 py-3 text-right text-blue-700 tabular-nums">
                                    ₱{formatNumber(companyGrandTotal)}
                                </td>
                                <td />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}

            {/* Cost Summary Cards */}
            {selectedItems.length > 0 && (
                <div className="flex gap-4 shrink-0">
                    <div className="flex-1 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                        <div className="text-xs text-gray-500 font-medium uppercase tracking-wide">
                            Total Supplier Cost
                        </div>
                        <div className="text-2xl font-bold text-gray-800 tabular-nums mt-0.5">
                            ₱{formatNumber(supplierGrandTotal)}
                        </div>
                    </div>
                    <div className="flex-1 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                        <div className="text-xs text-blue-600 font-medium uppercase tracking-wide">
                            Total Company Cost
                        </div>
                        <div className="text-2xl font-bold text-blue-700 tabular-nums mt-0.5">
                            ₱{formatNumber(companyGrandTotal)}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
